import ClinicalNoteProvenance from '@/features/clinical-actions/ClinicalNoteProvenance';
import VoiceDraftAmendment from '@/features/clinical-actions/VoiceDraftAmendment.jsx';
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Button from "@/components/ui/Button.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  buildVitalRepSoapNoteText,
  buildVitalRepSourceSnapshot,
  fetchVitalRepTranscriptRoughNotes,
  generateVitalRepSoapDraft,
  loadVitalRepNoteByAppointment,
  saveVitalRepAmendmentForAppointment,
  saveVitalRepBetaNoteForAppointment,
} from "@/pages/doctors/appointments/lib/vitalRepNote.js";

const VITALREP_AMENDMENT_DRAFT_PREFIX = "afrimed:vitalrep-amendment:";

function readAppointmentDraft(appointmentId) {
  const key = `afrimed:doctor_appt:${String(appointmentId || "")}`;
  try {
    const raw = sessionStorage.getItem(key) || localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function resolvePatientName(state = {}) {
  const patient = state.patient || state.patientProfile || state.appointment?.patient || {};
  return patient.full_name || patient.name || state.patientName || "Patient";
}

function resolvePatientId(state = {}) {
  const patient = state.patient || state.patientProfile || state.appointment?.patient || {};
  return (
    state.patientUserId ||
    state.patient_user_id ||
    state.patientId ||
    state.appointment?.patient_user_id ||
    state.appointment?.patientId ||
    state.appointment?.patient_id ||
    patient.user_id ||
    patient.id ||
    ""
  );
}

function resolveDoctorId(state = {}, authUser = null) {
  const doctor = state.doctor || state.appointment?.doctor || {};
  return (
    state.doctorUserId ||
    state.doctor_user_id ||
    state.doctorId ||
    state.appointment?.doctor_user_id ||
    state.appointment?.doctor_id ||
    doctor.user_id ||
    doctor.doctor_user_id ||
    authUser?.id ||
    ""
  );
}

function readVitalRepAmendmentDraft(appointmentId) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId || typeof window === "undefined") return null;
  try {
    const raw =
      window.sessionStorage.getItem(`${VITALREP_AMENDMENT_DRAFT_PREFIX}${cleanAppointmentId}`) ||
      window.localStorage.getItem(`${VITALREP_AMENDMENT_DRAFT_PREFIX}${cleanAppointmentId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeVitalRepAmendmentDraft(appointmentId, draft) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId || typeof window === "undefined") return;
  try {
    const payload = JSON.stringify({
      ...(draft || {}),
      startedAt: draft?.startedAt || new Date().toISOString(),
    });
    window.sessionStorage.setItem(`${VITALREP_AMENDMENT_DRAFT_PREFIX}${cleanAppointmentId}`, payload);
    window.localStorage.setItem(`${VITALREP_AMENDMENT_DRAFT_PREFIX}${cleanAppointmentId}`, payload);
  } catch {
    // Best-effort amendment continuity only.
  }
}

function clearVitalRepAmendmentDraft(appointmentId) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId || typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(`${VITALREP_AMENDMENT_DRAFT_PREFIX}${cleanAppointmentId}`);
    window.localStorage.removeItem(`${VITALREP_AMENDMENT_DRAFT_PREFIX}${cleanAppointmentId}`);
  } catch {
    // Best-effort cleanup only.
  }
}

const SOAP_FIELDS = [
  ["subjective", "Subjective"],
  ["objective", "Objective"],
  ["assessment", "Assessment"],
  ["plan", "Plan"],
  ["unsorted", "Unsorted clinical notes for review"],
];

const PROMINENT_TEXTAREA_CLASS =
  "w-full resize-y rounded-[12px] border border-transparent bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-3 text-body-large leading-[1.4] text-on-surface shadow-none outline-none transition focus:border-[rgba(var(--afrimed-primary),0.32)] placeholder:text-on-surface-var";

function ProminentTextAreaField({
  label,
  value,
  onChange,
  rows = 4,
  placeholder = "",
  className = "",
}) {
  return (
    <label className="block">
      <span className="text-title-small text-on-surface">{label}</span>
      <textarea
        value={value}
        onChange={onChange}
        rows={rows}
        className={`${PROMINENT_TEXTAREA_CLASS} mt-3 ${className}`}
        placeholder={placeholder}
      />
    </label>
  );
}

export default function DoctorVitalRepBeta() {
  const nav = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const mode = React.useMemo(() => new URLSearchParams(location.search || "").get("mode") || "", [location.search]);
  const requestedAmendMode = mode === "amend";
  const amendmentDraft = React.useMemo(() => readVitalRepAmendmentDraft(appointmentId), [appointmentId]);

  const routeState = location.state || {};
  const appointmentDraft = React.useMemo(
    () => routeState.appointment || routeState.returnState || readAppointmentDraft(appointmentId) || {},
    [appointmentId, routeState.appointment, routeState.returnState]
  );
  const patientName = resolvePatientName({ ...appointmentDraft, ...routeState });
  const patientUserId = resolvePatientId({ ...appointmentDraft, ...routeState });
  const doctorUserId = resolveDoctorId({ ...appointmentDraft, ...routeState }, user);
  const sourceSnapshot = React.useMemo(
    () =>
      buildVitalRepSourceSnapshot({
        appointmentId,
        restoredSnapshot: appointmentDraft,
        diagnosisTitle: appointmentDraft.diagnosisTitle,
        diagnosisNotes: appointmentDraft.diagnosisNotes,
        notes: appointmentDraft.notes,
      }),
    [appointmentDraft, appointmentId]
  );

  const [roughNotes, setRoughNotes] = React.useState("");
  const [summarySuggestion,setSummarySuggestion]=React.useState('');
  const [soapDraft, setSoapDraft] = React.useState(() => generateVitalRepSoapDraft(""));
  const [amendmentReason, setAmendmentReason] = React.useState(
    () => routeState.amendmentReason || amendmentDraft?.amendmentReason || ""
  );
  const [status, setStatus] = React.useState("");
  const [existingNote, setExistingNote] = React.useState(null);
  const [hydrated, setHydrated] = React.useState(false);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [transcriptPrefillChecked, setTranscriptPrefillChecked] = React.useState(false);
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const existingStatus = String(existingNote?.status || status || "").trim().toLowerCase();
  const hasFinalNote = ["final", "finalized"].includes(existingStatus) && Boolean(
    String(existingNote?.finalNoteText || "").trim()
  );
  const isAmendMode = requestedAmendMode && hydrated && hasFinalNote;

  React.useEffect(() => {
    let alive = true;
    async function hydrate() {
      try {
        const note = await loadVitalRepNoteByAppointment(appointmentId);
        if (!alive) return;
        setExistingNote(note || null);
        if (!note) return;
        setRoughNotes(amendmentDraft?.sourceTranscript || note.sourceTranscript || "");
        if (amendmentDraft?.soapDraft) setSoapDraft(amendmentDraft.soapDraft);
        else if (note.soapDraft) setSoapDraft(note.soapDraft);
        setStatus(note.status || "");
      } catch {
        if (!alive) return;
        setExistingNote(null);
        setLoadFailed(true);
      } finally {
        if (alive) setHydrated(true);
      }
    }
    setHydrated(false);
    hydrate();
    return () => {
      alive = false;
    };
  }, [amendmentDraft?.soapDraft, amendmentDraft?.sourceTranscript, appointmentId]);

  React.useEffect(() => {
    if (!requestedAmendMode || !hydrated || hasFinalNote) return;
    nav(`/doctors/appointments/${appointmentId}/vitalrep-beta`, {
      replace: true,
      state: routeState,
    });
  }, [appointmentId, hasFinalNote, hydrated, nav, requestedAmendMode, routeState]);

  React.useEffect(() => {
    let alive = true;
    async function hydrateTranscriptPrefill() {
      if (!hydrated || isAmendMode || String(roughNotes || "").trim()) {
        if (alive) setTranscriptPrefillChecked(true);
        return;
      }
      try {
        const transcriptText = await fetchVitalRepTranscriptRoughNotes({
          appointmentId,
          sessionId: routeState.sessionId || routeState.videoSessionId || appointmentDraft.videoSessionId || "",
        });
        if (!alive) return;
        if (transcriptText) {
          setRoughNotes(transcriptText);
          setSoapDraft(generateVitalRepSoapDraft(transcriptText));
        }
      } catch {
        // Manual paste/type remains the fallback when no transcript can be read.
      } finally {
        if (alive) setTranscriptPrefillChecked(true);
      }
    }
    hydrateTranscriptPrefill();
    return () => {
      alive = false;
    };
  }, [appointmentDraft.videoSessionId, appointmentId, hydrated, isAmendMode, roughNotes, routeState.sessionId, routeState.videoSessionId]);

  React.useEffect(() => {
    if (!isAmendMode) return;
    writeVitalRepAmendmentDraft(appointmentId, {
      amendmentReason,
      sourceTranscript: roughNotes,
      soapDraft,
      startedAt: amendmentDraft?.startedAt || new Date().toISOString(),
    });
  }, [amendmentDraft?.startedAt, amendmentReason, appointmentId, isAmendMode, roughNotes, soapDraft]);

  function generateDraft() {
    setError("");
    setSoapDraft(generateVitalRepSoapDraft(roughNotes));
    setStatus("draft");
  }

  function returnToAppointment(nextStatus = "") {
    const target = routeState.returnTo || `/doctors/appointments/${String(appointmentId)}`;
    nav(target, {
      replace: true,
      state: {
        ...(routeState.returnState || appointmentDraft || {}),
        vitalRepStatus: nextStatus || status || "",
      },
    });
  }

  async function save(nextStatus) {
    if (!doctorUserId) {
      setError(t("vitalrep.beta.errors.missingDoctor", "Doctor context is missing."));
      return;
    }
    const nextAmendmentReason = String(amendmentReason || "").trim();
    if (isAmendMode && !nextAmendmentReason) {
      setError(t("vitalrep.beta.errors.amendmentReason", "Add an amendment reason before saving this amendment."));
      return;
    }
    setBusy(nextStatus);
    setError("");
    try {
      const saved = isAmendMode
        ? await saveVitalRepAmendmentForAppointment({
            appointmentId,
            doctorUserId,
            patientUserId,
            sourceSnapshot,
            sourceTranscript: roughNotes,
            soapDraft,
            noteText: buildVitalRepSoapNoteText(soapDraft),
            amendmentReason: nextAmendmentReason,
            expectedFinalReportId: existingNote?.authorityReportId || null,
            endedAt: appointmentDraft.video_ended_at || appointmentDraft.endedAt || "",
          })
        : await saveVitalRepBetaNoteForAppointment({
            appointmentId,
            doctorUserId,
            patientUserId,
            sourceSnapshot,
            sourceTranscript: roughNotes,
            soapDraft,
            status: nextStatus,
            endedAt: appointmentDraft.video_ended_at || appointmentDraft.endedAt || "",
          });
      setStatus(saved?.status || nextStatus);
      if (saved?.soapDraft) setSoapDraft(saved.soapDraft);
      if (isAmendMode) clearVitalRepAmendmentDraft(appointmentId);
      returnToAppointment(saved?.status || nextStatus);
    } catch {
      setError(t("vitalrep.beta.errors.save", "Could not save this VitalRep™ note right now."));
    } finally {
      setBusy("");
    }
  }

  if (!hydrated || loadFailed) return <div className="afrimed-rail py-8" role="status">{t(loadFailed?'clinical.noteLoadFailed':'clinical.noteLoading')}</div>;

  return (
    <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]" data-hide-tabbar="true">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
          title={t("vitalrep.beta.title", "VitalRep™ beta")}
          subtitle={t("vitalrep.beta.subtitle", "Doctor-reviewed structured draft")}
          onBack={() =>
            returnToAppointment()
          }
          backIconSrc="/icons/doctor/appointments/arrow_back.svg"
        />

        <ClinicalNoteProvenance note={existingNote}/>
        <div className="mt-5 space-y-4">
          <section className="rounded-[12px] bg-[rgb(var(--clr-surface-container))] px-4 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.06)]">
            <div className="text-label-medium text-on-surface-var">
              {t("vitalrep.beta.context", "Patient • appointment context")}
            </div>
            <div className="mt-1 text-title-medium text-on-surface">{patientName}</div>
            <div className="mt-1 text-body-medium text-on-surface-var">
              {t("vitalrep.beta.appointment", "Appointment")} #{appointmentId}
            </div>
          </section>

          <section className="rounded-[12px] bg-[rgba(var(--afrimed-primary),0.06)] px-4 py-3">
            <div className="text-title-small text-on-surface">
              {isAmendMode
                ? t("vitalrep.beta.amendmentTitle", "Audited amendment")
                : t("vitalrep.beta.consentTitle", "Consent-aware beta")}
            </div>
            <div className="mt-1 text-body-medium leading-5 text-on-surface-var">
              {isAmendMode
                ? t(
                    "vitalrep.beta.amendmentBody",
                    "Review and update the SOAP fields. Saving creates a new audited amendment version; it does not silently overwrite the final note."
                  )
                : t(
                    "vitalrep.beta.consentBody",
                    "Use only with patient consent. The doctor must review, edit, and approve all clinical content before saving."
                  )}
            </div>
          </section>

          {isAmendMode ? (
            <ProminentTextAreaField
              label={t("vitalrep.beta.amendmentReason", "Amendment reason")}
              value={amendmentReason}
              onChange={(event) => setAmendmentReason(event.target.value)}
              rows={3}
              placeholder={t(
                "vitalrep.beta.amendmentReasonPlaceholder",
                "Explain why this finalised note needs an amendment."
              )}
            />
          ) : null}

          <section>
            <ProminentTextAreaField
              label={t("vitalrep.beta.input", "Paste or review transcript notes")}
              value={roughNotes}
              onChange={(event) => setRoughNotes(event.target.value)}
              rows={7}
              placeholder={t(
                "vitalrep.beta.inputPlaceholder",
                transcriptPrefillChecked
                  ? "Paste or type consent-based consultation notes here."
                  : "Checking for saved transcript notes..."
              )}
            />
            <div className="mt-2 text-body-small leading-5 text-on-surface-var">
              {t(
                "vitalrep.beta.folaSupport",
                "Fola can help structure notes, but the clinician remains responsible for the final record."
              )}
            </div>
            <Button
              type="button"
              size="lg"
              className="mt-3 min-h-[44px] w-full justify-center rounded-[12px]"
              onClick={generateDraft}
            >
              {t("vitalrep.beta.generate", "Generate SOAP Draft")}
            </Button>
          </section>

          <section>
            <div className="flex items-center justify-between gap-3">
              <div className="text-title-small text-on-surface">
                {t("vitalrep.beta.soapTitle", "SOAP Draft")}
              </div>
              {status ? (
                <div className="rounded-full bg-[rgba(0,189,196,0.12)] px-3 py-1 text-label-small text-[#008A8F]">
                  {status === "finalized"
                    ? t("vitalrep.beta.finalSaved", "Finalised")
                    : t("vitalrep.beta.draftSaved", "Draft")}
                </div>
              ) : null}
            </div>

            {(!hasFinalNote||isAmendMode)&&<div className="mt-3 space-y-2" data-testid="follow-up-assistance">
              <Button variant="tonal" disabled={status==='finalized'&&!isAmendMode} onClick={()=>setSummarySuggestion(String(soapDraft?.plan||''))}>{t('clinical.summaryAssist')}</Button>
              <p className="text-body-small text-on-surface-var">{t('clinical.summarySource')}</p>
              {summarySuggestion&&<><p className="whitespace-pre-wrap">{summarySuggestion}</p><Button variant="text" onClick={()=>{setSoapDraft(v=>({...v,plan:summarySuggestion}));setSummarySuggestion('');}}>{t('clinical.useSuggestion')}</Button><Button variant="text" onClick={()=>setSummarySuggestion('')}>{t('clinical.discardSuggestion')}</Button></>}
              <VoiceDraftAmendment onApply={text=>{setSummarySuggestion('');setSoapDraft(v=>({...v,plan:[v.plan,text].filter(Boolean).join('\n')}));}}/>
            </div>}
            <div className="mt-3 space-y-3">
              {SOAP_FIELDS.map(([key, label]) => (
                <ProminentTextAreaField
                  key={key}
                  label={label}
                  value={soapDraft?.[key] || ""}
                  onChange={(event) =>
                    setSoapDraft((current) => ({ ...(current || {}), [key]: event.target.value }))
                  }
                  rows={key === "unsorted" ? 3 : 4}
                />
              ))}
            </div>

            <div className="mt-3 rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-3 text-body-small leading-5 text-on-surface-var shadow-[0_1px_2px_rgba(15,23,42,0.06)]">
              {soapDraft?.reviewWarning ||
                t(
                  "vitalrep.beta.warning",
                  "Doctor review required: verify, edit, and approve all content before final sign-off."
                )}
            </div>
          </section>

          {error ? (
            <div className="text-body-small text-[rgb(var(--clr-error))]">{error}</div>
          ) : null}

          <div className="flex gap-3">
            {!isAmendMode ? (
              <Button
                type="button"
                variant="tonal"
                size="lg"
                className="min-h-[44px] flex-1 justify-center rounded-[12px]"
                onClick={() => save("draft")}
                disabled={Boolean(busy)}
              >
                {busy === "draft" ? t("common.saving", "Saving...") : t("vitalrep.beta.saveDraft", "Save draft")}
              </Button>
            ) : null}
            <Button
              type="button"
              size="lg"
              className="min-h-[44px] flex-1 justify-center rounded-[12px]"
              onClick={() => save("final")}
              disabled={Boolean(busy)}
            >
              {busy === "final"
                ? t("common.saving", "Saving...")
                : isAmendMode
                  ? t("vitalrep.beta.saveAmendment", "Save amendment")
                  : t("vitalrep.beta.finalize", "Finalise VitalRep™ note")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
